/**
 * Pointer drags, vertex editing, cutout shaping — and ED-14's arrow-key scope.
 *
 * Extracted from `MaskEditor.tsx` (WS-J J-10 / AUD-2 RC-4) with no change to a single body.
 *
 * The one thing to read before touching this file is the arrow-key effect. It used to listen on
 * `window`, so as long as ANY cutout existed the arrow keys were hijacked app-wide — scrolling
 * the templates page silently nudged mask geometry. ED-14's fix is FOCUS-OR-POINTER CONTAINMENT:
 * react only when focus is inside the editor root or the pointer is over it, and never when the
 * event target is an `INPUT` / `SELECT` / `TEXTAREA`. Both halves travelled verbatim, and
 * `use-mask-geometry.test.ts` pins all four of its rules.
 *
 * `Target` (the drag union) and `rectFromDiagonal` moved with the drag code; `MaskEditor.tsx`
 * imports the latter back for `defaultBrandRect`, which keeps every import one-directional.
 */
import { useEffect, useState, useRef } from "react";
import type { Mask, Point, Polygon } from "@genz/engine-bind";
import type { AppLogger } from "../app/context.js";
import { clamp01, rgbToHexColor } from "./pixels.js";
import { boundingBox, chaikin, insertOnNearestEdge, roundedRectPolygon, stepRadius, MAX_CORNER_RADIUS } from "./corners.js";
import { screenToNorm } from "./zoom.js";

export type Target =
  | { kind: "print"; index: number }
  | { kind: "brand"; index: number }
  // Drag the WHOLE brand box: translate all four corners by (pointer − start).
  | { kind: "brand-move"; start: Point; original: Polygon }
  | { kind: "cutout"; cutout: number; index: number }
  // Drag the WHOLE cutout: translate every vertex by (pointer − start).
  | { kind: "cutout-move"; cutout: number; start: Point; original: Polygon }
  | { kind: "print-move"; start: Point; original: Polygon }
  // Pan the zoomed view: shift the viewBox center by the pointer delta (#3).
  | { kind: "pan"; startClient: Point; startCenter: Point };

/** Build an axis-aligned rectangle [TL, TR, BR, BL] spanning two opposite corners — keeps the
 *  brand-logo box a true rectangle (no freeform quad) however a corner handle is dragged. */
export function rectFromDiagonal(a: Point, b: Point): Polygon {
  const minX = Math.min(a.x, b.x), maxX = Math.max(a.x, b.x);
  const minY = Math.min(a.y, b.y), maxY = Math.max(a.y, b.y);
  return [
    { x: minX, y: minY },
    { x: maxX, y: minY },
    { x: maxX, y: maxY },
    { x: minX, y: maxY },
  ];
}

/**
 * A default print-area rectangle to seed manual drawing: the "Seed rectangle" action.
 */
export const DEFAULT_PRINT_AREA: Polygon = [
  { x: 0.15, y: 0.15 },
  { x: 0.85, y: 0.15 },
  { x: 0.85, y: 0.85 },
  { x: 0.15, y: 0.85 },
];

export interface UseMaskGeometryArgs {
  locked?: boolean;
  /** The editor is mounted but not on screen: the arrow keys are not its own. */
  inactive?: boolean;
  /** WS-AUTOKEY fix round — a customer's placement-only canvas: the print-area corners move,
   *  nothing is added, and no cutout is moved (the arrow keys nudge a selected corner only). */
  placementOnly?: boolean;
  logger?: AppLogger;
  /** Geometry the drags mutate, straight from the props. */
  printArea: Polygon;
  cutouts: Mask[];
  brandArea?: Polygon | null;
  onPrintAreaChange: (p: Polygon) => void;
  onCutoutsChange: (m: Mask[]) => void;
  onBrandAreaChange?: (p: Polygon) => void;

  /** Resolved display size + the zoom/pan viewBox (J-4) — every screen↔normal map goes through it. */
  w: number;
  h: number;
  vb: { x: number; y: number; w: number; h: number };
  svgRef: React.RefObject<SVGSVGElement | null>;
  setCenter: (c: Point) => void;

  /** The eyedropper (J-6's `readSource` reads the pixel under the click). */
  readSource: () => { data: Uint8ClampedArray; width: number; height: number } | null;
  markerEyedrop: boolean;
  setMarkerEyedrop: (v: boolean) => void;
  setMarkerColor: (c: [number, number, number] | null) => void;
  flashPickNotice: (msg: string) => void;
}

/** `polygon` moved by (dx, dy), stopped at the canvas edge so its shape never changes. */
export function shiftInside(polygon: Polygon, dx: number, dy: number): Polygon {
  const xs = polygon.map((p) => p.x);
  const ys = polygon.map((p) => p.y);
  const cdx = Math.max(-Math.min(...xs), Math.min(1 - Math.max(...xs), dx));
  const cdy = Math.max(-Math.min(...ys), Math.min(1 - Math.max(...ys), dy));
  return polygon.map((p) => ({ x: p.x + cdx, y: p.y + cdy }));
}

export function useMaskGeometry(args: UseMaskGeometryArgs) {
  const {
    locked, inactive, placementOnly, logger, printArea, cutouts, brandArea,
    onPrintAreaChange, onCutoutsChange, onBrandAreaChange, w, h,
    vb, svgRef, setCenter, readSource, markerEyedrop,
    setMarkerEyedrop, setMarkerColor, flashPickNotice,
  } = args;

  /** ED-14 — the editor's root element + whether the pointer is over it. Together with focus
   *  containment these scope the arrow-key nudge handler to the editor instead of the whole page. */
  const rootRef = useRef<HTMLDivElement>(null);
  const pointerInsideRef = useRef(false);
  const [drag, setDrag] = useState<Target | null>(null);
  /**
   * Whether the print area is SELECTED. Its translucent blue fill tints the artwork underneath,
   * which makes it impossible to judge the design's real colours while placing it — so the fill
   * is now shown only while the area is selected. Deselected, the area is still fully visible as
   * its outline plus corner handles; nothing is hidden, it just stops colouring the design.
   * Selecting = clicking the area or one of its corners; clicking anywhere else deselects.
   */
  const [printAreaActive, setPrintAreaActive] = useState(false);
  const [activeCutout, setActiveCutout] = useState<number>(0);
  /** Index of the currently selected PRINT-AREA vertex (for arrow-key nudge), or null.
   *  Set on print-vertex pointer-down; cleared when a cutout is grabbed instead. */
  const [selectedPrintVertex, setSelectedPrintVertex] = useState<number | null>(null);

  const toNorm = (clientX: number, clientY: number): Point => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    // Map through the current zoom/pan viewBox so handle drags stay pixel-accurate (#3).
    return screenToNorm(clientX, clientY, rect, vb, w, h);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag) return;
    // Panning is allowed even when locked (it only moves the view, not the geometry).
    if (drag.kind === "pan") {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect || !rect.width || !rect.height) return;
      // Drag content with the pointer: moving right shifts the view left.
      const normDx = ((e.clientX - drag.startClient.x) / rect.width) * (vb.w / w);
      const normDy = ((e.clientY - drag.startClient.y) / rect.height) * (vb.h / h);
      setCenter({
        x: clamp01(drag.startCenter.x - normDx),
        y: clamp01(drag.startCenter.y - normDy),
      });
      return;
    }
    if (locked) return;
    const pt = toNorm(e.clientX, e.clientY);
    if (drag.kind === "print") {
      const next = printArea.map((p, i) => (i === drag.index ? pt : p));
      onPrintAreaChange(next);
    } else if (drag.kind === "brand") {
      // Rectangle-constrained resize: rebuild an axis-aligned box from the dragged corner and
      // the (fixed) opposite corner, so the brand box is never a freeform quad.
      if (brandArea && brandArea.length === 4 && onBrandAreaChange) {
        const opposite = brandArea[(drag.index + 2) % 4];
        onBrandAreaChange(
          rectFromDiagonal({ x: clamp01(pt.x), y: clamp01(pt.y) }, opposite),
        );
      }
    } else if (drag.kind === "brand-move") {
      // Translate the whole rectangle by the pointer delta, clamped so no corner leaves canvas.
      if (onBrandAreaChange) {
        onBrandAreaChange(shiftInside(drag.original, pt.x - drag.start.x, pt.y - drag.start.y));
      }
    } else if (drag.kind === "print-move") {
      onPrintAreaChange(shiftInside(drag.original, pt.x - drag.start.x, pt.y - drag.start.y));
    } else if (drag.kind === "cutout-move") {
      // Translate the whole cutout by the pointer delta, clamped so no vertex
      // leaves the canvas (shifts the entire shape, not a single point).
      const dx = pt.x - drag.start.x;
      const dy = pt.y - drag.start.y;
      const moved = drag.original.map((p) => ({ x: clamp01(p.x + dx), y: clamp01(p.y + dy) }));
      const next = cutouts.map((c, ci) => (ci === drag.cutout ? { ...c, polygon: moved } : c));
      onCutoutsChange(next);
    } else {
      const next = cutouts.map((c, ci) =>
        ci === drag.cutout ? { ...c, polygon: c.polygon.map((p, i) => (i === drag.index ? pt : p)) } : c,
      );
      onCutoutsChange(next);
    }
  };

  const endDrag = () => setDrag(null);

  /** Sample the marker colour from the source pixel under a normalized click (eyedropper). */
  const sampleMarkerColor = (norm: Point) => {
    const src = readSource();
    if (!src) return;
    const x = Math.max(0, Math.min(src.width - 1, Math.round(norm.x * src.width)));
    const y = Math.max(0, Math.min(src.height - 1, Math.round(norm.y * src.height)));
    const i = (y * src.width + x) * 4;
    const rgb: [number, number, number] = [src.data[i], src.data[i + 1], src.data[i + 2]];
    // If the picked pixel is essentially pure magenta, keep the ROBUST default keyer
    // (markerColor=null → the hue-stable min(R,B)−G metric) instead of the Manhattan-distance
    // picked-colour path, which keys + fits the print-area quad less accurately. Only a
    // genuinely DRIFTED marker colour switches to the picked path.
    const dMagenta = Math.abs(rgb[0] - 255) + rgb[1] + Math.abs(rgb[2] - 255);
    const nearMagenta = dMagenta <= 60;
    setMarkerColor(nearMagenta ? null : rgb);
    setMarkerEyedrop(false);
    flashPickNotice(nearMagenta
      ? "That's the marker colour — the automatic keyer already handles it. Click the product image itself to key a drifted colour."
      : `Keying ${rgbToHexColor(rgb)}`);
    logger?.log("info", `Editor · Marker colour picked (${rgbToHexColor(rgb)})${nearMagenta ? " — near magenta, using the robust default metric" : ""}`);
  };

  // Add a vertex on the print-area edge nearest the click (or draw-from-scratch).
  const onBackgroundClick = (e: React.MouseEvent) => {
    if (locked) return;
    // Eyedropper mode intercepts the click to sample the marker colour.
    if (markerEyedrop) {
      sampleMarkerColor(toNorm(e.clientX, e.clientY));
      return;
    }
    if (!(e.altKey || e.shiftKey)) return; // alt/shift to add a point (vs drag)
    if (placementOnly) return; // a customer moves corners; they never add one
    const pt = toNorm(e.clientX, e.clientY);
    // ED-13: INSERT on the nearest edge instead of appending. Appending put the new vertex at the
    // end of the ring, so clicking anywhere but near the last edge produced a self-intersecting
    // polygon that `geometryValidate` rejected — Save blocked by what looked like "adding a point".
    if (e.shiftKey && cutouts[activeCutout]) {
      const next = cutouts.map((c, ci) =>
        ci === activeCutout ? { ...c, polygon: insertOnNearestEdge(c.polygon, pt) } : c,
      );
      onCutoutsChange(next);
    } else {
      onPrintAreaChange(insertOnNearestEdge(printArea, pt));
    }
  };

  const deleteVertex = (t: Target) => {
    if (locked) return;
    if (t.kind === "print") {
      if (printArea.length <= 3) return; // keep a valid polygon
      onPrintAreaChange(printArea.filter((_, i) => i !== t.index));
      // Keep the nudge selection valid after a delete (clear it; it'd point at a moved index).
      setSelectedPrintVertex(null);
    } else if (t.kind === "cutout") {
      // ED-13: the same minimum-vertex guard the print area has. Without it a cutout could be
      // double-clicked down to 2 (or 0) points — a degenerate mask the engine can't render.
      const poly = cutouts[t.cutout]?.polygon;
      if (!poly || poly.length <= 3) return;
      onCutoutsChange(
        cutouts.map((c, ci) =>
          ci === t.cutout ? { ...c, polygon: c.polygon.filter((_, i) => i !== t.index) } : c,
        ),
      );
    }
  };

  const addCutout = () => {
    if (locked) return;
    const c: Mask = {
      polygon: [
        { x: 0.45, y: 0.45 },
        { x: 0.55, y: 0.45 },
        { x: 0.55, y: 0.55 },
        { x: 0.45, y: 0.55 },
      ],
    };
    onCutoutsChange([...cutouts, c]);
    setActiveCutout(cutouts.length);
  };

  /** Delete the ENTIRE active cutout (not just one vertex). Keeps the active index in
   *  range by clamping to the new last cutout. */
  const deleteActiveCutout = () => {
    if (locked) return;
    if (!cutouts[activeCutout]) return;
    const next = cutouts.filter((_, ci) => ci !== activeCutout);
    onCutoutsChange(next);
    setActiveCutout((a) => Math.max(0, Math.min(a, next.length - 1)));
  };

  /** Seed a default print-area rectangle when none is drawn yet — gives the admin a quick
   *  rectangle to trace instead of click-drawing. */
  const seedPrintArea = () => {
    if (locked) return;
    onPrintAreaChange(DEFAULT_PRINT_AREA);
  };

  /** Add a primitive cutout shape (centered) — an ellipse (camera lens) or a
   *  rounded rectangle (button/notch). Generated as a polygon via roundedRectPolygon
   *  so it needs no engine/wire change. */
  const addShapeCutout = (kind: "ellipse" | "rounded") => {
    if (locked) return;
    const bbox = { minX: 0.42, minY: 0.42, maxX: 0.58, maxY: 0.58 };
    const radius = kind === "ellipse" ? MAX_CORNER_RADIUS : MAX_CORNER_RADIUS * 0.35;
    onCutoutsChange([...cutouts, { polygon: roundedRectPolygon(bbox, radius) }]);
    setActiveCutout(cutouts.length);
  };

  /** Smooth the active cutout into a curve (Chaikin corner-cutting) — gives
   *  Bézier-like rounded shapes while staying a plain polygon the engine renders. */
  const smoothActiveCutout = () => {
    if (locked) return;
    const c = cutouts[activeCutout];
    if (!c || c.polygon.length < 3) return;
    onCutoutsChange(cutouts.map((cc, ci) => (ci === activeCutout ? { ...cc, polygon: chaikin(cc.polygon) } : cc)));
  };

  // Arrow keys nudge by 1px (Shift = 10px). When a PRINT-AREA vertex is selected they
  // nudge that single vertex (via onPrintAreaChange); otherwise they nudge the ACTIVE
  // cutout. Keeps focus-free precision control without dragging. Ignored while locked or
  // typing in a field.
  useEffect(() => {
    if (locked || inactive) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      // ED-14: SCOPE the handler to the editor. It listened on `window`, so as long as ANY cutout
      // existed the arrow keys were hijacked app-wide — a user scrolling the templates page or
      // stepping through a list silently nudged mask geometry. React only when the editor owns
      // the interaction: focus is inside it, or the pointer is over it.
      const root = rootRef.current;
      const focusInside = !!root && typeof document !== "undefined"
        && document.activeElement instanceof Node && root.contains(document.activeElement);
      if (!focusInside && !pointerInsideRef.current) return;
      const step = (e.shiftKey ? 10 : 1) / 400; // normalized 1px / 10px nudge
      let dx = 0, dy = 0;
      if (e.key === "ArrowLeft") dx = -step;
      else if (e.key === "ArrowRight") dx = step;
      else if (e.key === "ArrowUp") dy = -step;
      else if (e.key === "ArrowDown") dy = step;
      else return;
      // Print-area vertex selected → nudge just that vertex.
      if (selectedPrintVertex != null && printArea[selectedPrintVertex]) {
        e.preventDefault();
        onPrintAreaChange(
          printArea.map((p, i) =>
            i === selectedPrintVertex ? { x: clamp01(p.x + dx), y: clamp01(p.y + dy) } : p,
          ),
        );
        return;
      }
      // The print area selected, no corner: nudge the whole area, and the design with it.
      if (printAreaActive && printArea.length) {
        e.preventDefault();
        onPrintAreaChange(shiftInside(printArea, dx, dy));
        return;
      }
      if (placementOnly) return; // no cutout is the customer's to move
      const c = cutouts[activeCutout];
      if (!c) return;
      e.preventDefault();
      onCutoutsChange(cutouts.map((cc, ci) =>
        ci === activeCutout
          ? { ...cc, polygon: cc.polygon.map((p) => ({ x: clamp01(p.x + dx), y: clamp01(p.y + dy) })) }
          : cc,
      ));
    };
    if (typeof window !== "undefined") window.addEventListener("keydown", onKey);
    return () => { if (typeof window !== "undefined") window.removeEventListener("keydown", onKey); };
  }, [cutouts, activeCutout, locked, inactive, placementOnly, onCutoutsChange, selectedPrintVertex, printArea, printAreaActive, onPrintAreaChange]);

  /**
   * Adjust the active cutout's corner radius and regenerate its polygon as a
   * rounded rectangle of its current bounding box. `set` overrides instead of
   * stepping (used by "Off"). The submitted geometry is just the new polygon.
   */
  const adjustCornerRadius = (delta: number, set?: number) => {
    if (locked) return;
    const c = cutouts[activeCutout];
    if (!c) return;
    const bbox = boundingBox(c.polygon);
    const radius = set !== undefined ? set : stepRadius(c.cornerRadius, delta);
    const polygon = roundedRectPolygon(bbox, radius);
    onCutoutsChange(cutouts.map((cc, ci) => (ci === activeCutout ? { ...cc, cornerRadius: radius, polygon } : cc)));
  };

  return {
    rootRef, pointerInsideRef,
    drag, setDrag,
    printAreaActive, setPrintAreaActive,
    activeCutout, setActiveCutout,
    selectedPrintVertex, setSelectedPrintVertex,
    toNorm, onPointerMove, endDrag,
    sampleMarkerColor, onBackgroundClick,
    deleteVertex, addCutout, deleteActiveCutout, seedPrintArea, addShapeCutout,
    smoothActiveCutout, adjustCornerRadius,
  };
}
